import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import type { SavedViewVisibility } from "../../api/client.ts";

export interface ViewContext {
  tenantId?: string | null;
  tenantName?: string | null;
  organizationId?: string | null;
  organizationName?: string | null;
}

interface SaveViewDialogProps {
  open: boolean;
  /** Editing an existing view (rename / change who sees it); creating otherwise. */
  initial?: { name: string; visibility: SavedViewVisibility };
  context: ViewContext;
  pending: boolean;
  onSave: (name: string, visibility: SavedViewVisibility) => void;
  onClose: () => void;
}

/** Name a view and pick who sees it: only me, everyone in the tenant, or in the org. */
export default function SaveViewDialog({ open, initial, context, pending, onSave, onClose }: SaveViewDialogProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [visibility, setVisibility] = useState<SavedViewVisibility>(initial?.visibility ?? "private");
  const trimmed = name.trim();

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="xs" fullWidth>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed) onSave(trimmed, visibility);
        }}
      >
        <DialogTitle>{initial ? "Edit view" : "Save view"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { maxLength: 120 } }}
            sx={{ mt: 1, mb: 2 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Who can use it
          </Typography>
          <RadioGroup value={visibility} onChange={(e) => setVisibility(e.target.value as SavedViewVisibility)}>
            <FormControlLabel value="private" control={<Radio size="small" />} label="Only me" />
            {context.tenantId && (
              <FormControlLabel
                value="tenant"
                control={<Radio size="small" />}
                label={`Everyone in ${context.tenantName ?? "this tenant"}`}
              />
            )}
            {context.organizationId && (
              <FormControlLabel
                value="organization"
                control={<Radio size="small" />}
                label={`Everyone in ${context.organizationName ?? "this organization"}`}
              />
            )}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={pending} disabled={!trimmed}>
            {initial ? "Save" : "Save view"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
